import { Link } from 'react-router-dom';
import { formatMetric } from '@data/utils/format-metric';
import { LoadingBar } from '@data/components/basic/LoadingBar/LoadingBar';
import type { SessionEdAppMetrics } from '@data/hooks/api/use-metrics-sessions-v2-api/use-metrics-sessions-v2-api';

// ── Sub-components ─────────────────────────────────────────────────────────────

function MetricDisplay({
  label,
  value,
  loading = true,
}: {
  label: string;
  value: string;
  loading?: boolean;
}) {
  return (
    <div className="flex-1">
      {loading ? (
        <LoadingBar className="h-16" />
      ) : (
        <div className="gap-8">
          <p className="mb-[2px] text-[14px] tablet:text-[16px]">{label}</p>
          <p className="text-2xl tablet:text-3xl">{value}</p>
        </div>
      )}
    </div>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const CARD_BASE_STYLES =
  'rounded-lg border border-solid border-[#D4D4D4] bg-white px-4 py-6 shadow-md tablet:p-8';

const SUMMARY_CARD_STYLES =
  'block h-full rounded-lg bg-white px-4 py-6 shadow-md tablet:p-5';

// ── Summary card (no metrics) ──────────────────────────────────────────────────

interface ProductSummaryCardProps {
  applicationLabel: string;
  applicationIconSrc?: string;
  gradeDisplay?: string;
  tags?: readonly string[];
  description?: string;
  to?: string;
}

function ProductSummaryCard({
  applicationLabel,
  applicationIconSrc,
  gradeDisplay,
  tags = [],
  description,
  to,
}: ProductSummaryCardProps) {
  const content = (
    <>
      {applicationIconSrc && (
        <div className="mb-4 h-10">
          <img
            src={applicationIconSrc}
            alt={`${applicationLabel} logo`}
            className="h-full object-contain"
          />
        </div>
      )}
      <h3 className="mb-1 text-lg font-semibold text-neutral-1300">
        {applicationLabel}
      </h3>
      {(gradeDisplay || tags.length > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {gradeDisplay && (
            <p className="text-sm text-neutral-1300">
              <span className="font-semibold">Grade</span> {gradeDisplay}
            </p>
          )}
          {tags.length > 0 && (
            <ul className="flex flex-wrap gap-2" aria-label="Product access">
              {tags.map((tag) => (
                <li
                  key={tag}
                  className="rounded-sm bg-brand-grey_7 px-1.5 py-0.5 text-xs text-neutral-900"
                >
                  {tag}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {description && (
        <p className="mt-2 text-sm text-neutral-1300">{description}</p>
      )}
    </>
  );

  if (!to) return <div className={SUMMARY_CARD_STYLES}>{content}</div>;

  return (
    <Link
      to={to}
      className={`${SUMMARY_CARD_STYLES} transition-shadow focus-ring hover:shadow-lg`}
    >
      {content}
    </Link>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

/**
 * This product's slice of the page-level session-metrics call.
 *
 * `values` is undefined when the response carries no bucket for the product —
 * a product the teacher holds that reported no sessions at all, and equally the
 * case where the request failed. That is not an error state of its own: the card
 * falls through to the same "no product data" panel a zero value produces.
 */
export interface ProductCardSessionMetrics {
  values: SessionEdAppMetrics | undefined;
  isLoading: boolean;
}

interface ProductCardProps {
  applicationCode: string;
  applicationLabel: string;
  applicationIconSrc?: string;
  gradeDisplay?: string;
  /**
   * Resolved by the page, which fetches every product's metrics in one request.
   * The card issues none of its own. Omit it for the summary card (the admin
   * landing page), which shows no metrics.
   */
  sessionMetrics?: ProductCardSessionMetrics;
  /** Summary card only: the product description. */
  description?: string;
  /**
   * Summary card only: access tags, e.g. the products' access type
   * ("Educator only"). More tags are to come from the applications endpoint.
   */
  tags?: readonly string[];
  /** Summary card only: where the card links to. Omit for a plain card. */
  to?: string;
}

/**
 * A product on a Data and Insights landing page.
 *
 * With `sessionMetrics` it is the teacher card: the product's session metrics,
 * linking to its Overview report when there is data. Without them it is the
 * summary card: logo, grade, description and optional tags, linking to `to`.
 */
export function ProductCard({
  applicationCode,
  applicationLabel,
  applicationIconSrc,
  gradeDisplay,
  sessionMetrics,
  description,
  tags,
  to,
}: ProductCardProps) {
  if (!sessionMetrics) {
    return (
      <ProductSummaryCard
        applicationLabel={applicationLabel}
        applicationIconSrc={applicationIconSrc}
        gradeDisplay={gradeDisplay}
        description={description}
        tags={tags}
        to={to}
      />
    );
  }

  const { values, isLoading: loading } = sessionMetrics;

  const sessions = formatMetric(values?.sessions);
  const minutes = formatMetric(values?.minutes);
  const hasNoData = !loading && (!values?.sessions || values.sessions === '0');

  const cardContent = (
    <>
      {loading ? (
        <div role="status" aria-label="Loading">
          <LoadingBar className="mb-4 h-10 w-28" />
          <LoadingBar className="mb-3 h-4 w-16" />
          <LoadingBar className="mb-2 h-4" />
        </div>
      ) : (
        <>
          <div className="mb-4 h-10">
            <img
              src={applicationIconSrc}
              alt={`${applicationLabel} icon`}
              className="h-full object-contain"
            />
          </div>
          <h3 className="mb-[2px] text-2xl font-semibold text-[#333] tablet:text-xl">
            {applicationLabel}
          </h3>
          {gradeDisplay && (
            <p className="text-sm font-semibold text-[#333]">
              Grade{' '}
              <span className="text-[16px] font-normal">{gradeDisplay}</span>
            </p>
          )}
        </>
      )}

      {hasNoData ? (
        <div className="mt-[10px] rounded border border-dashed border-neutral-400 p-2">
          <p className="text-xs font-bold leading-[18px] text-neutral-700">
            No product data available
          </p>
          <p className="text-xs leading-[18px] text-neutral-700">
            Data will automatically be populated as students use the product
            digitally.
          </p>
        </div>
      ) : (
        <div className="mt-[10px] flex gap-8 font-semibold text-neutral-1300">
          <MetricDisplay
            label="Student Sessions"
            value={sessions}
            loading={loading}
          />
          <MetricDisplay
            label="Student Minutes"
            value={minutes}
            loading={loading}
          />
        </div>
      )}
    </>
  );

  if (loading || hasNoData) {
    return (
      <div className={`cursor-default ${CARD_BASE_STYLES}`}>{cardContent}</div>
    );
  }

  return (
    <Link
      to={`/data/${applicationCode}/overview`}
      className={`${CARD_BASE_STYLES} transition-shadow hover:shadow-lg`}
    >
      {cardContent}
    </Link>
  );
}
